import { CalendarIcon, ClockIcon, FlagIcon } from "@xola/icons";
import { Dot } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate, isExperiencePurchaseItem, isStoreCreditPurchaseItem, Loader } from "common";
import React from "react";
import { useHistory } from "react-router-dom";
import { Experience, ExperiencePurchaseItem, Purchase } from "types";
import { useResource } from "../../hooks/useResource";
import { getCustomerName } from "../../models/Purchase";
import { Currency } from "../Common/Currency";
import { NoTranslate } from "../Common/NoTranslate";
import { PurchaseProductImage } from "../PurchasesMenu/PurchaseProductImage";
import { useMenuSidebarStore } from "@/stores/menuSidebarStore";

interface NotificationPurchaseTileProps {
    purchase: Purchase;
    paymentTitle: string;
    amount: number;
    item?: ExperiencePurchaseItem;
    isClickable?: boolean;
}

export const NotificationPurchaseTile = ({
    purchase,
    paymentTitle,
    amount,
    item: initialPurchaseItem,
    isClickable = true,
}: NotificationPurchaseTileProps) => {
    const item = initialPurchaseItem ?? purchase.items[0];
    const isExperiencePurchase = isExperiencePurchaseItem(item);

    const history = useHistory();
    const { data: experience, isLoading } = useResource<Experience>(["products", item.product?.id], null, {
        enabled: isExperiencePurchase,
    });
    const isMist = purchase.items.length > 1;
    const productName = isExperiencePurchase
        ? experience?.name
        : isStoreCreditPurchaseItem(item)
        ? "Store Credit"
        : "Gift";

    const handleTileClick = () => {
        if (purchase.id && isClickable) {
            const link = isMist ? `/purchases/${purchase.id}` : `/purchases/${purchase.id}/${item.id}`;
            history.push(link);
            useMenuSidebarStore.setState({ shouldCloseDrawer: true });
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div
            className={clsx("rounded-lg bg-gray-lighter", isClickable && "hover: cursor-pointer")}
            data-testid="notification-purchase-tile"
            onClick={handleTileClick}
        >
            <div className="w-full border-b border-gray-light py-1 px-3 font-bold">
                <FlagIcon size="medium" className="mr-1" />{" "}
                <NoTranslate data-testid="notification-purchase-tile-customer-name">
                    {getCustomerName(purchase)}
                </NoTranslate>
            </div>

            <div className="mb-2 flex items-center justify-between divide-x-2 divide-gray-light p-4">
                <div className="mr-5 flex space-x-1 rounded-lg">
                    <div className="relative mr-3 flex">
                        <PurchaseProductImage purchase={purchase} size="medium" />
                    </div>

                    <div className="flex w-full items-center">
                        {isMist && !initialPurchaseItem ? (
                            <div className="font-bold leading-4">Multiple Items</div>
                        ) : (
                            <div className="flex-col">
                                <div
                                    className="no-translate font-bold leading-4"
                                    data-testid="notification-purchase-tile-experience-name"
                                >
                                    {productName}
                                </div>
                                {isExperiencePurchase && item.arrivalDateTime ? (
                                    <div className="flex text-gray-darker">
                                        <span className="space-x-1">
                                            <CalendarIcon />
                                            <NoTranslate data-testid="notification-purchase-tile-arrival-date">
                                                {formatDate(item.arrivalDateTime, DateFormat.DATE)}
                                            </NoTranslate>
                                        </span>
                                        {item.arrivalTime ? (
                                            <div>
                                                <Dot color="secondary" className="mr-1 ml-1" />
                                                <NoTranslate>
                                                    <ClockIcon /> {formatDate(item.arrivalDateTime, DateFormat.TIME)}
                                                </NoTranslate>
                                            </div>
                                        ) : null}
                                    </div>
                                ) : null}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-center pl-5">
                    <NoTranslate as="h1">
                        <Currency>{Math.abs(amount)}</Currency>
                    </NoTranslate>
                    <div>{paymentTitle}</div>
                </div>
            </div>
        </div>
    );
};
